/**
 * VO2Max Report Service
 * Handles generation of VO2Max report HTML for preview and PDF generation
 * Also provides API functions for fetching and submitting VO2Max reports
 */

import api from '../utils/api';

// API Service functions
export const vo2MaxReportService = {
  // Get VO2Max report by appointment ID
  getReportByAppointmentId: async (appointmentId) => {
    try {
      console.log(`Calling GET /admin/vo2max-reports/by-appointment/${appointmentId}`);
      const response = await api.get(`/admin/vo2max-reports/by-appointment/${appointmentId}`);
      console.log('GET response:', response);
      return response.data; // Return the data directly
    } catch (error) {
      console.error('Error fetching VO2Max report:', error);
      throw error;
    }
  },
  
  // Create a new VO2Max report
  createReport: async (reportData) => {
    try {
      // Extract the PDF URL if available
      const pdfUrl = reportData.pdf_url;
      console.log('PDF URL from reportData:', pdfUrl);
      
      // Create a clean data object without nested structures
      const cleanData = {
        ...reportData,
        // Explicitly set PDF URL at the top level
        pdf_url: pdfUrl
      };
      
      // Create the payload with the clean data
      const payload = {
        data: cleanData
      };
      
      // Log the full payload for debugging
      console.log('Full payload JSON:', JSON.stringify(payload));
      console.log('PDF URL in payload:', payload.data.pdf_url);
      
      // Make the API request
      console.log('Calling POST /admin/vo2max-reports');
      const response = await api.post('/admin/vo2max-reports', payload);
      
      // Log the response
      console.log('POST response status:', response.status);
      console.log('POST response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating VO2Max report:', error);
      throw error;
    }
  },
  
  // Update an existing VO2Max report
  updateReport: async (reportId, reportData) => {
    try {
      console.log(`Calling PUT /admin/vo2max-reports/${reportId} with data:`, reportData);
      const response = await api.put(`/admin/vo2max-reports/${reportId}`, { data: reportData });
      console.log('PUT response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating VO2Max report:', error);
      throw error;
    }
  }
};

export function getVo2Maxcolor(rank) {
  switch (rank) {
    case 1:
      return 'very-low';
    case 2:
      return 'low';
    case 3:
      return 'fair';
    case 4:
      return 'good';
    case 5:
      return 'excellent';
    case 6:
      return 'superior';
    default:
      return 'very-low';
  }
}

export function getVo2Maxtitle(rank) {
  switch (rank) {
    case 1:
      return 'Your vo2 max is very low';
    case 2:
      return 'Your vo2 max is low';
    case 3:
      return 'Your vo2 max is fair';
    case 4:
      return 'Your vo2 max is good';
    case 5:
      return 'Your vo2 max is excellent';
    case 6:
      return 'Your vo2 max is superior';
    default:
      return 'Your vo2 max is very low';
  }
}

export function getVo2MaxDescription(rank, vo2max) {
  switch (rank) {
    case 1:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, placing you in the ‘Very Low’ range for your age (below the 20ᵗʰ percentile). This indicates limited cardiovascular capacity and suggests a high potential for improvement through foundational aerobic training.';
    case 2:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, which falls in the ‘Low’ range for your age (approximately the 20ᵗʰ–35ᵗʰ percentile). While adequate for daily activity, it indicates room for significant improvement in endurance and energy efficiency.';
    case 3:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, placing you in the ‘Fair’ range for your age (between the 35ᵗʰ and 50ᵗʰ percentile). This level of fitness supports most daily and recreational activities but can be improved for better stamina and performance.';
    case 4:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, which places you in the ‘Good’ range for your age (between the 50ᵗʰ and 70ᵗʰ percentile). This means your cardiovascular fitness is functional and supports daily activity well—but there’s still room to optimize for better endurance, fat metabolism, and energy efficiency.';
    case 5:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, placing you in the ‘Excellent’ range for your age (between the 70ᵗʰ and 85ᵗʰ percentile). This reflects strong aerobic performance and energy capacity. With specific training, you could reach elite levels.';
    case 6:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, which places you in the ‘Superior’ range for your age (above the 85ᵗʰ percentile). This represents elite-level cardiovascular fitness with excellent endurance potential and metabolic efficiency.';
    default:
      return 'Your VO₂ Max is ' + vo2max + ' ml/kg/min, placing you in the ‘Very Low’ range for your age (below the 20ᵗʰ percentile). This indicates limited cardiovascular capacity and suggests a high potential for improvement through foundational aerobic training.';
  }
}

export const getDataTable = (reportData) => {
  let html = "";
  if (reportData.vo2_data_table && reportData.vo2_data_table.length > 0) {
    // Prepare data for Mustache templating
    console.log(reportData.vo2_data_table);
    const vo2DataForTemplate = reportData.vo2_data_table.map(row => ({
      stage: row.stage || '',
      time: row.time || '',
      hr: row.hr || '',
      o2: row.incline || '0.0', // Map incline to O2 column
      rer: row.speed || '',      // Map speed to RER column
      ve: row.vo2_ml || '',      // Map ve_vo2 to VE column
      vo2: row.vo2_lkg || '', // Use ml/kg/min value for VO2 column
      vevo2: row.ve_vo2,
      calories: row.calories_min || '', // Use calories/min for Calories column
      cadence: row.calories_hr || '' // Use calories/hr for Cadence column
    }));

    vo2DataForTemplate.forEach((row, index) => {
      html += `<tr>
      <td>${row.stage}</td>
      <td>${row.time}</td>
      <td>${row.hr}</td>
      <td>${row.o2}</td>
      <td>${row.rer}</td>
      <td>${row.ve}</td>
      <td>${row.vo2}</td>
      <td>${row.vevo2}</td>
      </tr>
      `;
    });

  }

    return html;

}


export const getDataTableLables = (reportData) => {
  let lable = [];
  let hr = [];
  let vo2 = [];
  if (reportData.vo2_data_table && reportData.vo2_data_table.length > 0) {
    // Prepare data for Mustache templating
    console.log(reportData.vo2_data_table);
    reportData.vo2_data_table.forEach(row => {
      lable.push(row.time);
      hr.push(row.hr);
      vo2.push(row.vo2_lkg);
    });

  }

  

    return { lable, hr, vo2};

}

export const anaerobicThresholdDisplay = (reportData) => {
  let atDisplay1 = "display2";
  let atDisplay2 = "display2";
  let atDisplay3 = "display2";
  let vtDisplay3 = "display2";
  let vtDisplay4 = "display2";
  let vtDisplay5 = "display2";

  let atDisplay = "1";
  let vtDisplay = "5";

  
  console.log(reportData.training_zones?.zone1_bpm, reportData.training_zones?.zone2_bpm, reportData.training_zones?.zone3_bpm, reportData.training_zones?.zone4_bpm, reportData.training_zones?.zone5_bpm, reportData.aerobic_threshold_hr, reportData.anaerobic_threshold_hr);

  if (reportData.training_zones?.zone1_bpm && reportData.aerobic_threshold_hr && reportData.anaerobic_threshold_hr) {
    // Split the zone1_bpm range (e.g., "120-140") into min and max values
    const [zone2Min, zone2Max] = reportData.training_zones?.zone2_bpm.split('-').map(Number);
    const [zone3Min, zone3Max] = reportData.training_zones?.zone3_bpm.split('-').map(Number);
    const [zone4Min, zone4Max] = reportData.training_zones?.zone4_bpm.split('-').map(Number);
    const aerobicHR = Number(reportData.aerobic_threshold_hr);
    const anaerobicHR = Number(reportData.anaerobic_threshold_hr);

    console.log( zone2Min, zone2Max, zone3Min, zone3Max, zone4Min, zone4Max, aerobicHR, anaerobicHR);
      // if (aerobicHR >= zone1Min && aerobicHR <= zone1Max) {
      //   atDisplay1 = "display1";
      // }
      if (aerobicHR > zone2Min && aerobicHR <= zone2Max) {
        atDisplay2 = "display1 ";
        atDisplay = "2";
      }
      if (aerobicHR > zone3Min && aerobicHR <= zone3Max) {
        atDisplay3 = "display1";
        atDisplay = "3";
      }
       if(anaerobicHR > zone3Min && anaerobicHR <= zone3Max){
        vtDisplay3 = "display1";
        vtDisplay = "3";
       }
       if(anaerobicHR > zone4Min && anaerobicHR <= zone4Max){
        vtDisplay4 = "display1";
        vtDisplay = "4";
       }
      //  if(anaerobicHR >= zone5Min && anaerobicHR <= zone5Max){
      //   vtDisplay5 = "display1";
      //  }

       console.log( atDisplay2, atDisplay3, vtDisplay3, vtDisplay4);
    // Check if zone1_bpm range falls between aerobic and anaerobic thresholds
  }

  return {
    atDisplay1,
    atDisplay2,
    atDisplay3,
    vtDisplay3,
    vtDisplay4,
    vtDisplay5,
    atDisplay,
    vtDisplay
  };
}
  
    
  

/**
 * Generate HTML for VO2Max report preview
 * @param {Object} reportData - The VO2Max report data
 * @returns {Promise<string>} - HTML content for the report
 */
export const generateVO2MaxReportHTML = async (reportData) => {
  try {

    
    // const normativeData = {
    //   female: {
    //       '13-19': [{ category: 'Very Poor', rank: 1, min: 17.5, max: 25.0 }, { category: 'Poor', rank: 2, min: 25.0, max: 30.9 }, { category: 'Fair', rank: 3, min: 31.0, max: 34.9 }, { category: 'Good', rank: 4, min: 35.0, max: 38.9 }, { category: 'Excellent', rank: 5, min: 39.0, max: 41.9 }, { category: 'Superior', rank: 6, min: 41.9, max: 52.4 }],
    //       '20-29': [{ category: 'Very Poor', rank: 1, min: 16.5, max: 23.6 }, { category: 'Poor', rank: 2, min: 23.6, max: 28.9 }, { category: 'Fair', rank: 3, min: 29.0, max: 32.9 }, { category: 'Good', rank: 4, min: 33.0, max: 36.9 }, { category: 'Excellent', rank: 5, min: 37.0, max: 41.0 }, { category: 'Superior', rank: 6, min: 41.0, max: 51.3 }],
    //       '30-39': [{ category: 'Very Poor', rank: 1, min: 16.0, max: 22.8 }, { category: 'Poor', rank: 2, min: 22.8, max: 26.9 }, { category: 'Fair', rank: 3, min: 27.0, max: 31.4 }, { category: 'Good', rank: 4, min: 31.5, max: 35.6 }, { category: 'Excellent', rank: 5, min: 35.7, max: 40.0 }, { category: 'Superior', rank: 6, min: 40.0, max: 50.0 }],
    //       '40-49': [{ category: 'Very Poor', rank: 1, min: 14.7, max: 21.0 }, { category: 'Poor', rank: 2, min: 21.0, max: 24.4 }, { category: 'Fair', rank: 3, min: 24.5, max: 28.9 }, { category: 'Good', rank: 4, min: 29.0, max: 32.8 }, { category: 'Excellent', rank: 5, min: 32.9, max: 36.9 }, { category: 'Superior', rank: 6, min: 36.9, max: 46.1 }],
    //       '50-59': [{ category: 'Very Poor', rank: 1, min: 14.1, max: 20.2 }, { category: 'Poor', rank: 2, min: 20.2, max: 22.7 }, { category: 'Fair', rank: 3, min: 22.8, max: 26.9 }, { category: 'Good', rank: 4, min: 27.0, max: 31.4 }, { category: 'Excellent', rank: 5, min: 31.5, max: 35.7 }, { category: 'Superior', rank: 6, min: 35.7, max: 44.6 }],
    //       '60+': [{ category: 'Very Poor', rank: 1, min: 12.3, max: 17.5 }, { category: 'Poor', rank: 2, min: 17.5, max: 20.1 }, { category: 'Fair', rank: 3, min: 20.2, max: 24.4 }, { category: 'Good', rank: 4, min: 24.5, max: 30.2 }, { category: 'Excellent', rank: 5, min: 30.3, max: 31.4 }, { category: 'Superior', rank: 6, min: 31.4, max: 39.3 }],
    //   },
    //   male: {
    //       '13-19': [{ category: 'Very Poor', rank: 1, min: 24.5, max: 35.0 }, { category: 'Poor', rank: 2, min: 35.0, max: 38.3 }, { category: 'Fair', rank: 3, min: 38.4, max: 45.1 }, { category: 'Good', rank: 4, min: 45.2, max: 50.9 }, { category: 'Excellent', rank: 5, min: 51.0, max: 55.9 }, { category: 'Superior', rank: 6, min: 55.9, max: 69.9 }],
    //       '20-29': [{ category: 'Very Poor', rank: 1, min: 23.1, max: 33.0 }, { category: 'Poor', rank: 2, min: 33.0, max: 36.4}, { category: 'Fair', rank: 3, min: 36.5, max: 42.4 }, { category: 'Good', rank: 4, min: 42.5, max: 46.4 }, { category: 'Excellent', rank: 5, min: 46.5, max: 52.4 }, { category: 'Superior', rank: 6, min: 52.4, max: 65.5 }],
    //       '30-39': [{ category: 'Very Poor', rank: 1, min: 22.1, max: 31.5 }, { category: 'Poor', rank: 2, min: 31.5, max: 35.4 }, { category: 'Fair', rank: 3, min: 35.5, max: 40.9 }, { category: 'Good', rank: 4, min: 41.0, max: 44.9 }, { category: 'Excellent', rank: 5, min: 45.0, max: 49.4 }, { category: 'Superior', rank: 6, min: 49.4, max: 61.8 }],
    //       '40-49': [{ category: 'Very Poor', rank: 1, min: 21.1, max: 30.2 }, { category: 'Poor', rank: 2, min: 30.2, max: 33.5 }, { category: 'Fair', rank: 3, min: 33.6, max: 38.9 }, { category: 'Good', rank: 4, min: 39.0, max: 43.7 }, { category: 'Excellent', rank: 5, min: 43.8, max: 48.0 }, { category: 'Superior', rank: 6, min: 48.0, max: 60.0 }],
    //       '50-59': [{ category: 'Very Poor', rank: 1, min: 18.3, max: 26.1 }, { category: 'Poor', rank: 2, min: 26.1, max: 30.9 }, { category: 'Fair', rank: 3, min: 31.0, max: 35.7 }, { category: 'Good', rank: 4, min: 35.8, max: 40.9 }, { category: 'Excellent', rank: 5, min: 41.0, max: 45.3 }, { category: 'Superior', rank: 6, min: 45.3, max: 56.6 }],
    //       '60+': [{ category: 'Very Poor', rank: 1, min: 14.4, max: 20.5 }, { category: 'Poor', rank: 2, min: 20.5, max: 26.0 }, { category: 'Fair', rank: 3, min: 26.1, max: 32.2 }, { category: 'Good', rank: 4, min: 32.3, max: 36.4 }, { category: 'Excellent', rank: 5, min: 36.5, max: 44.2 }, { category: 'Superior', rank: 6, min: 44.2, max: 55.3 }],
    //   }
    // };

    const normativeData = {
      female: {
        // Note: The image starts at age 20-29. 
        '14-29': [{ category: 'Very Low', rank: 1, min: (23.9*.75).toFixed(1), max: 23.9 },{ category: 'Low', rank: 2, min: 23.9, max: 30.5 },{ category: 'Fair', rank: 3, min: 30.5, max: 37.6 },{ category: 'Good', rank: 4, min: 37.6, max: 44.7 },{ category: 'Excellent', rank: 5, min: 44.7, max: 56.0 },{ category: 'Superior', rank: 6, min: 56.0, max: (56*1.25).toFixed(1) }],
        '30-39': [{ category: 'Very Low', rank: 1, min: (20.9*.75).toFixed(1), max: 20.9 },{ category: 'Low', rank: 2, min: 20.9, max: 25.3 },{ category: 'Fair', rank: 3, min: 25.3, max: 30.2 },{ category: 'Good', rank: 4, min: 30.2, max: 36.1 },{ category: 'Excellent', rank: 5, min: 36.1, max: 45.8 },{ category: 'Superior', rank: 6, min: 45.8, max: (45.8*1.25).toFixed(1) }],
        '40-49': [{ category: 'Very Low', rank: 1, min: (18.8*.75).toFixed(1), max: 18.8 },{ category: 'Low', rank: 2, min: 18.8, max: 22.1 },{ category: 'Fair', rank: 3, min: 22.1, max: 26.7 },{ category: 'Good', rank: 4, min: 26.7, max: 32.4 },{ category: 'Excellent', rank: 5, min: 32.4, max: 41.7 },{ category: 'Superior', rank: 6, min: 41.7, max: (41.7*1.25).toFixed(1) }],
        '50-59': [{ category: 'Very Low', rank: 1, min: (17.3*.75).toFixed(1), max: 17.3 },{ category: 'Low', rank: 2, min: 17.3, max: 19.9 },{ category: 'Fair', rank: 3, min: 19.9, max: 23.4 },{ category: 'Good', rank: 4, min: 23.4, max: 27.6 },{ category: 'Excellent', rank: 5, min: 27.6, max: 35.9 },{ category: 'Superior', rank: 6, min: 35.9, max: (35.9*1.25).toFixed(1) }],
        '60-69': [{ category: 'Very Low', rank: 1, min: (14.6*.75).toFixed(1), max: 14.6 },{ category: 'Low', rank: 2, min: 14.6, max: 17.2 },{ category: 'Fair', rank: 3, min: 17.2, max: 20.0 },{ category: 'Good', rank: 4, min: 20.0, max: 23.8 },{ category: 'Excellent', rank: 5, min: 23.8, max: 29.4 },{ category: 'Superior', rank: 6, min: 29.4, max: (29.4*1.25).toFixed(1) }],
        '70-79': [{ category: 'Very Low', rank: 1, min: (13.6*.75).toFixed(1), max: 13.6 },{ category: 'Low', rank: 2, min: 13.6, max: 15.6 },{ category: 'Fair', rank: 3, min: 15.6, max: 18.3 },{ category: 'Good', rank: 4, min: 18.3, max: 20.8 },{ category: 'Excellent', rank: 5, min: 20.8, max: 24.1 },{ category: 'Superior', rank: 6, min: 24.1, max: (24.1*1.25).toFixed(1) }]
      },
      male: {
        // Note: The image starts at age 20-29. 
        '14-29': [{ category: 'Very Low', rank: 1, min: (32.1*.75).toFixed(1), max: 32.1 },{ category: 'Low', rank: 2, min: 32.1, max: 40.1 },{ category: 'Fair', rank: 3, min: 40.1, max: 48.0 },{ category: 'Good', rank: 4, min: 48.0, max: 55.2 },{ category: 'Excellent', rank: 5, min: 55.2, max: 66.3 },{ category: 'Superior', rank: 6, min: 66.3, max: (66.3*1.25).toFixed(1) }],
        '30-39': [{ category: 'Very Low', rank: 1, min: (30.2*.75).toFixed(1), max: 30.2 },{ category: 'Low', rank: 2, min: 30.2, max: 35.9 },{ category: 'Fair', rank: 3, min: 35.9, max: 42.4 },{ category: 'Good', rank: 4, min: 42.4, max: 49.2 },{ category: 'Excellent', rank: 5, min: 49.2, max: 59.8 },{ category: 'Superior', rank: 6, min: 59.8, max: (59.8*1.25).toFixed(1) }],
        '40-49': [{ category: 'Very Low', rank: 1, min: (26.8*.75).toFixed(1), max: 26.8 },{ category: 'Low', rank: 2, min: 26.8, max: 31.9 },{ category: 'Fair', rank: 3, min: 31.9, max: 37.8 },{ category: 'Good', rank: 4, min: 37.8, max: 45.0 },{ category: 'Excellent', rank: 5, min: 45.0, max: 55.6 },{ category: 'Superior', rank: 6, min: 55.6, max: (55.6*1.25).toFixed(1) }],
        '50-59': [{ category: 'Very Low', rank: 1, min: (22.8*.75).toFixed(1), max: 22.8 },{ category: 'Low', rank: 2, min: 22.8, max: 27.1 },{ category: 'Fair', rank: 3, min: 27.1, max: 32.6 },{ category: 'Good', rank: 4, min: 32.6, max: 39.7 },{ category: 'Excellent', rank: 5, min: 39.7, max: 50.7 },{ category: 'Superior', rank: 6, min: 50.7, max: (50.7*1.25).toFixed(1) }],
        '60-69': [{ category: 'Very Low', rank: 1, min: (19.8*.75).toFixed(1), max: 19.8 },{ category: 'Low', rank: 2, min: 19.8, max: 23.7 },{ category: 'Fair', rank: 3, min: 23.7, max: 28.2 },{ category: 'Good', rank: 4, min: 28.2, max: 34.5 },{ category: 'Excellent', rank: 5, min: 34.5, max: 43.0 },{ category: 'Superior', rank: 6, min: 43.0, max: (43.0*1.25).toFixed(1) }],
        '70-79': [{ category: 'Very Low', rank: 1, min: (17.1*.75).toFixed(1), max: 17.1 },{ category: 'Low', rank: 2, min: 17.1, max: 20.4 },{ category: 'Fair', rank: 3, min: 20.4, max: 24.4 },{ category: 'Good', rank: 4, min: 24.4, max: 30.4 },{ category: 'Excellent', rank: 5, min: 30.4, max: 39.7 },{ category: 'Superior', rank: 6, min: 39.7, max: (39.7*1.25).toFixed(1) }]
      }
    };
    
  /**
   * Calculates the VO2 Max fitness category based on age, gender, and VO2 Max value.
   * @param {string} gender - The gender ('male' or 'female').
   * @param {number} age - The age of the person.
   * @param {number} vo2max - The VO2 Max value in ml/kg/min.
   * @returns {Object} Object containing rank and category information.
   */
  function getVo2MaxCategory(gender, age, vo2max) {
    // Default response
    const defaultResponse = { rank: 1, category: [] };
    
    // Validate inputs
    if (!gender || !vo2max || !age) {
      return defaultResponse;
    }

    // Ensure gender is lowercase and valid
    const normalizedGender = String(gender).toLowerCase();
    if (!normativeData[normalizedGender]) {
      return defaultResponse;
    }

    // Find the appropriate age category
    let ageCategory = null;
    const ageRanges = Object.keys(normativeData[normalizedGender]);
    
    for (const range of ageRanges) {
      if (range.includes('+')) {
        const minAge = parseInt(range.replace('+', ''), 10);
        if (age >= minAge) {
          ageCategory = range;
          break;
        }
      } else {
        const [minAge, maxAge] = range.split('-').map(Number);
        if (age >= minAge && age <= maxAge) {
          ageCategory = range;
          break;
        }
      }
    }

    if (!ageCategory) {
      return defaultResponse;
    }

    // Get categories for the age group
    const categories = normativeData[normalizedGender][ageCategory] || [];
    
    // Find the matching category
    const foundCategory = categories.find(cat => {
      const min = cat.min !== undefined ? cat.min : -Infinity;
      const max = cat.max !== undefined ? cat.max : Infinity;
      return vo2max >= min && vo2max <= max;
    });

    return {
      rank: foundCategory ? foundCategory.rank : 1,
      category: categories
    };
  }
  


    console.log("VO2Max reportData", reportData);
    
    // Fetch the template file
    const response = await fetch('/templates/vo2max_ranjith.html');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.status}`);
    }
    
    let template = await response.text();
    
    // Helper function to format date as "2ND APRIL 2025"
    function formatDate(dateString) {
      if (!dateString || dateString.trim() === '' || isNaN(Date.parse(dateString))) {
        return 'N/A';
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();
      
      // Get day suffix (st, nd, rd, th)
      const suffix = getDaySuffix(day);
      
      return `${day}${suffix} ${month} ${year}`;
    }
    
    // Helper function to get the day suffix
    function getDaySuffix(day) {
      if (day > 3 && day < 21) return 'TH';
      switch (day % 10) {
        case 1: return 'ST';
        case 2: return 'ND';
        case 3: return 'RD';
        default: return 'TH';
      }
    }
    
    // Process VO2 data table for Mustache templating
    let processedTemplate = template;
    
    // Handle VO2 data table with Mustache-compatible format
    if (reportData.vo2_data_table && reportData.vo2_data_table.length > 0) {
      // Prepare data for Mustache templating
      const vo2DataForTemplate = reportData.vo2_data_table.map(row => ({
        stage: row.stage || '',
        time: row.time || '',
        hr: row.hr || '',
        o2: row.incline || '0.0', // Map incline to O2 column
        rer: row.speed || '',      // Map speed to RER column
        ve: row.ve_vo2 || '',      // Map ve_vo2 to VE column
        vo2: row.vo2_ml_kg_min || '', // Use ml/kg/min value for VO2 column
        calories: row.calories_min || '', // Use calories/min for Calories column
        cadence: row.calories_hr || '' // Use calories/hr for Cadence column
      }));
      
      // Replace the Mustache section markers with actual data
      const tableStart = processedTemplate.indexOf('{{#vo2_data_table}}');
      const tableEnd = processedTemplate.indexOf('{{/vo2_data_table}}');
      
      if (tableStart !== -1 && tableEnd !== -1) {
        const beforeTable = processedTemplate.substring(0, tableStart);
        const tableTemplate = processedTemplate.substring(
          tableStart + '{{#vo2_data_table}}'.length,
          tableEnd
        );
        const afterTable = processedTemplate.substring(
          tableEnd + '{{/vo2_data_table}}'.length
        );
        
        // Generate table rows
        let tableRows = '';
        vo2DataForTemplate.forEach(row => {
          let rowHtml = tableTemplate;
          Object.entries(row).forEach(([key, value]) => {
            rowHtml = rowHtml.replace(new RegExp(`{{${key}}}`, 'g'), value);
          });
          tableRows += rowHtml;
        });
        
        // Remove the fallback section if we have data
        const fallbackStart = afterTable.indexOf('{{^vo2_data_table}}');
        const fallbackEnd = afterTable.indexOf('{{/vo2_data_table}}', fallbackStart);
        
        if (fallbackStart !== -1 && fallbackEnd !== -1) {
          processedTemplate = beforeTable + tableRows + 
            afterTable.substring(0, fallbackStart) + 
            afterTable.substring(fallbackEnd + '{{/vo2_data_table}}'.length);
        } else {
          processedTemplate = beforeTable + tableRows + afterTable;
        }
      }
    } else {
      // If no data table, remove the dynamic section markers but keep the fallback content
      processedTemplate = processedTemplate
        .replace('{{#vo2_data_table}}', '')
        .replace('{{/vo2_data_table}}', '')
        .replace('{{^vo2_data_table}}', '')
        .replace('{{/vo2_data_table}}', '');
    }
    let categoriesValue = getVo2MaxCategory(reportData.patient_gender, reportData.patient_age, reportData.vo2max_value)

    console.log("categoriesValue", categoriesValue);
    console.log("categoriesValue", reportData.patient_gender);
    console.log("categoriesValue", reportData.patient_age);
    console.log("categoriesValue", reportData.vo2max_value);

    let zone5_bpm_to_peek = reportData.training_zones?.zone4_bpm.split("-")[0]+"-"+reportData.heart_rate_max;
    
    // Replace all placeholders with actual data
    const replacements = {
      // Patient Information
      '{{patient_name}}': reportData.patient_name || 'N/A',
      '{{patient_age}}': reportData.patient_age || 'N/A',
      '{{patient_gender}}': reportData.patient_gender || 'N/A',
      '{{patient_id}}': reportData.patient_id || 'N/A',
      '{{report_id}}': reportData.report_id || reportData.patient_id || 'N/A',
      '{{exam_date}}': formatDate(reportData.exam_date) || 'N/A',
      
      // Logo and fingerprint images
      '{{logo_url}}': reportData.logo_url || '/images/logo.png',
      '{{fingerprint_url}}': reportData.fingerprint_url || '/images/fingerprint.png',
      
      // VO2 Max and Heart Rate Data
      '{{vo2max_value}}': reportData.vo2max_value || 'N/A',
      '{{target_vo2max_value}}': (parseFloat(reportData.vo2max_value) + (parseFloat(reportData.vo2max_value) *.1)).toFixed(2) || 'N/A',
      '{{vo2max_unit}}': reportData.vo2max_unit || 'ml/kg/min',
      '{{heart_rate_max}}': reportData.heart_rate_max || 'N/A',
      '{{vo_max_category}}': JSON.stringify(categoriesValue.category),
      '{{vo_max_rank}}': categoriesValue.rank,
      '{{vo_max_color}}': getVo2Maxcolor(categoriesValue.rank),
      '{{vo_max_title}}': getVo2Maxtitle(categoriesValue.rank),
      '{{vo_max_description}}': getVo2MaxDescription(categoriesValue.rank, reportData.vo2max_value),
      
      // Recovery data
      '{{recovery_1min}}': reportData.training_zones?.recovery_1min || 'N/A',
      '{{recovery_1min_percent}}': reportData.training_zones?.recovery_1min_percent || '0',
      '{{recovery_2min}}': reportData.training_zones?.recovery_2min || 'N/A',
      '{{recovery_2min_percent}}': reportData.training_zones?.recovery_2min_percent || '0',
      
      // Training Zones - Replace hardcoded values in the template with dynamic values
      '{{zone1_bpm}}': reportData.training_zones?.zone1_bpm || '<138',
      '{{zone2_bpm}}': reportData.training_zones?.zone2_bpm || '138-164',
      '{{zone3_bpm}}': reportData.training_zones?.zone3_bpm || '164-175',
      '{{zone4_bpm}}': reportData.training_zones?.zone4_bpm || '175-176',
      '{{zone5_bpm}}': reportData.training_zones?.zone5_bpm || '>176',
      '{{zone5_bpm_to_peek}}': zone5_bpm_to_peek,
      
      '{{aerobic_threshold_2_display}}': reportData.training_zones?.zone1_bpm || '<138',
      '{{aerobic_threshold_3_display}}': reportData.training_zones?.zone1_bpm || '<138',
      '{{anaerobic_threshold_3_display}}': reportData.training_zones?.zone2_bpm || '138-164',
      '{{anaerobic_threshold_4_display}}': reportData.training_zones?.zone2_bpm || '138-164',
      '{{anaerobic_threshold_5_display}}': reportData.training_zones?.zone2_bpm || '138-164',
      

      // Threshold data
      '{{aerobic_threshold_hr}}': reportData.aerobic_threshold_hr || '127',
      '{{aerobic_threshold_vo2}}': reportData.aerobic_threshold_vo2 || '127',
      '{{aerobic_threshold_percent}}': reportData.aerobic_threshold_percent || '65%',
      '{{lactate_threshold_hr}}': reportData.lactate_threshold_hr || '157',
      '{{lactate_threshold_percent}}': reportData.lactate_threshold_percent || '80%',
      '{{anaerobic_threshold_hr}}': reportData.anaerobic_threshold_hr || '176',
      '{{anaerobic_threshold_vo2}}': reportData.anaerobic_threshold_vo2 || '176',
      '{{anaerobic_threshold_percent}}': reportData.anaerobic_threshold_percent || '90%',
      
      '{{atDisplay1}}': anaerobicThresholdDisplay(reportData).atDisplay1,
      '{{atDisplay2}}': anaerobicThresholdDisplay(reportData).atDisplay2,
      '{{atDisplay3}}': anaerobicThresholdDisplay(reportData).atDisplay3,
      '{{vtDisplay3}}': anaerobicThresholdDisplay(reportData).vtDisplay3,
      '{{vtDisplay4}}': anaerobicThresholdDisplay(reportData).vtDisplay4,
      '{{vtDisplay5}}': anaerobicThresholdDisplay(reportData).vtDisplay5,
      '{{atDisplay}}': anaerobicThresholdDisplay(reportData).atDisplay,
      '{{vtDisplay}}': anaerobicThresholdDisplay(reportData).vtDisplay,
      
      // Goal value for progress calculation
      '{{goal_value}}': reportData.goal_value || '51.6',
      '{{datatable}}': getDataTable(reportData),
      '{{datatable_graph}}': JSON.stringify(getDataTableLables(reportData)),
      '{{datatablelabels}}': getDataTableLables(reportData).lable,
      '{{datatablehr}}': getDataTableLables(reportData).hr,
      '{{datatablevo2}}': getDataTableLables(reportData).vo2,
      
      // Graph Image (if available)
      '{{vo2max_graph}}': reportData.vo2max_graph || '/images/placeholder-graph.png',
    };
    
    // Replace all placeholders in the template
    Object.entries(replacements).forEach(([key, value]) => {
      template = template.replace(new RegExp(key, 'g'), value);
    });
    
    return template;
  } catch (error) {
    console.error('Error generating VO2Max report:', error);
    throw error;
  }
};
